import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, User, FileText, Trash2, MessageSquare, Image as ImageIcon, File, Bell, Star, ZoomIn, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../contexts/ChatContext';
import { useAuth } from '../../contexts/AuthContext';
import { chatApi, getApiBaseUrl } from '../../services/cloudApi';
import ImageModal from './ImageModal';
import DeleteModal from '../Common/DeleteModal';
import Username, { getDisplayName } from '../Common/Username';
import { getIntlLocale } from '../../utils/dateLocale';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name[0].toUpperCase();
};

const FriendInfoSidebar = ({ friend, isOpen, onClose }) => {
  const { t, i18n } = useTranslation('chat');
  const dateLocaleTag = getIntlLocale(i18n.language);
  const { deleteFriend, deleteMessages } = useChat();
  const { authToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [mediaFiles, setMediaFiles] = useState({ images: [], documents: [], notes: [] });
  const [activeTab, setActiveTab] = useState('media');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  useEffect(() => {
    if (isOpen && friend && authToken) loadMediaFiles();
  }, [isOpen, friend?.id, authToken]);

  useEffect(() => {
    if (friend) setActiveTab('media');
  }, [friend?.id]);

  const loadMediaFiles = async () => {
    if (!friend || !authToken) return;
    setIsLoading(true);
    try {
      const friendId = friend.email || friend.id;
      const result = await chatApi.getMessages({ token: authToken, friendId });
      if (result?.ok && Array.isArray(result.messages)) {
        const images = [], documents = [], notes = [];
        result.messages.forEach(msg => {
          if (msg.type === 'image' && msg.file_url) {
            images.push({ id: msg.id, url: msg.file_url, createdAt: msg.created_at || msg.createdAt || msg.timestamp });
          } else if (msg.type === 'file' && msg.file_url) {
            documents.push({ id: msg.id, url: msg.file_url, fileName: msg.file_name || msg.fileName || msg.content || t('forwardModal.typeFallback.file'), createdAt: msg.created_at || msg.createdAt || msg.timestamp });
          } else if (msg.type === 'shared_note' || msg.type === 'note_share' || (msg.noteId || msg.note_id)) {
            notes.push({ id: msg.id, noteTitle: msg.noteTitle || msg.note_title || msg.content || t('forwardModal.typeFallback.note'), noteId: msg.noteId || msg.note_id, createdAt: msg.created_at || msg.createdAt || msg.timestamp });
          }
        });
        setMediaFiles({ images, documents, notes });
      }
    } catch (error) {
      console.error('Load media files error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const openPhoto = async (url) => {
    if (!url) return;
    let resolved = url;
    if (!url.startsWith('http') && !url.startsWith('blob:') && !url.startsWith('data:')) {
      const apiBase = await getApiBaseUrl();
      resolved = apiBase.replace('/api', '') + url;
    }
    setSelectedImageUrl(resolved);
    setShowImageModal(true);
  };

  const handleDeleteFriend = async () => {
    if (!friend) return;
    await deleteFriend(friend.id);
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleClearMessages = async () => {
    if (!friend) return;
    await deleteMessages(friend.id);
    setShowClearConfirm(false);
    onClose();
  };

  if (!isOpen || !friend) return null;

  const avatarUrl = friend.avatarUrl || friend.avatar_url;
  const friendName = getDisplayName(friend);

  const sidebarContent = (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            key="friend-info-sidebar"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 220 }}
            className="fixed right-0 top-0 h-full w-[300px] bg-[#0b1220] border-l border-white/[0.06] z-[10000] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-[52px] border-b border-white/[0.05] flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-5 h-5 rounded-full border border-[#3d8ef0]/60 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-[#3d8ef0]">i</span>
                </div>
                <span className="text-sm font-semibold text-[#c8d8eb]">{t('friendInfoSidebar.detailsTitle')}</span>
              </div>
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/[0.05] text-[#4d6a8a] hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto">
              {/* Avatar + Name */}
              <div className="px-5 pt-6 pb-5">
                <div className="flex items-start gap-4">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    <button
                      className="relative block focus:outline-none group"
                      onClick={() => openPhoto(avatarUrl)}
                    >
                      <div className="w-[60px] h-[60px] rounded-full overflow-hidden ring-2 ring-white/[0.07] bg-[#1e2d47] flex items-center justify-center text-lg font-bold text-[#3d8ef0]">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={friendName} className="w-full h-full object-cover" />
                        ) : getInitials(friendName)}
                      </div>
                      {avatarUrl && (
                        <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                          <ZoomIn className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </button>
                    {/* Online dot */}
                    <div className="absolute bottom-0.5 right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0b1220]" />
                  </div>

                  {/* Name & email */}
                  <div className="flex-1 min-w-0 pt-1">
                    <h2 className="text-[17px] font-bold text-white leading-tight truncate">
                      <Username user={friend} variant="default" />
                    </h2>
                    <p className="text-[12px] text-[#4d6a8a] mt-0.5 truncate">{friend.email}</p>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="px-5 pb-5">
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { icon: MessageSquare, label: t('friendInfoSidebar.quickActions.message'), onClick: onClose },
                    { icon: Bell, label: t('friendInfoSidebar.quickActions.mute'), onClick: () => {} },
                    { icon: Star, label: t('friendInfoSidebar.quickActions.starred'), onClick: () => setActiveTab('notes') },
                    { icon: FileText, label: t('friendInfoSidebar.quickActions.notes'), onClick: () => setActiveTab('notes') },
                  ].map(action => (
                    <button
                      key={action.label}
                      onClick={action.onClick}
                      className="flex flex-col items-center gap-2 py-3.5 px-1 rounded-xl bg-[#161f31] hover:bg-[#1a2640] transition-colors"
                    >
                      <action.icon className="w-[18px] h-[18px] text-[#3d8ef0]" />
                      <span className="text-[9px] font-medium text-[#8aaccc] text-center leading-tight">{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Stats / Tab switchers */}
              <div className="px-5 pb-5">
                <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest mb-3">{t('friendInfoSidebar.sharedContentLabel')}</p>
                <div className="grid grid-cols-3 gap-2.5">
                  {[
                    { id: 'media', label: t('friendInfoSidebar.stats.media'), value: mediaFiles.images.length },
                    { id: 'files', label: t('friendInfoSidebar.stats.files'), value: mediaFiles.documents.length },
                    { id: 'notes', label: t('friendInfoSidebar.stats.sharedNotes'), value: mediaFiles.notes.length },
                  ].map(stat => (
                    <button
                      key={stat.id}
                      onClick={() => setActiveTab(stat.id)}
                      className={`flex flex-col items-center p-4 rounded-xl transition-all ${activeTab === stat.id ? 'bg-[#1e2d47] ring-1 ring-[#3d8ef0]/25' : 'bg-[#161f31] hover:bg-[#1a2640]'}`}
                    >
                      <p className="text-[15px] font-bold text-white leading-none">{isLoading ? '–' : stat.value}</p>
                      <p className="text-[10px] text-[#4d6a8a] mt-1">{stat.label}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-white/[0.05] mb-5" />

              {/* Dynamic content */}
              <div className="px-5 pb-5">
                {activeTab === 'media' && (
                  <div>
                    <div className="flex items-center justify-between mb-2.5">
                      <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest">{t('friendInfoSidebar.mediaLabel')}</p>
                      {mediaFiles.images.length > 0 && <span className="text-[11px] text-[#4d6a8a]">{t('friendInfoSidebar.itemCount', { count: mediaFiles.images.length })}</span>}
                    </div>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-5 h-5 border-2 border-[#3d8ef0] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : mediaFiles.images.length > 0 ? (
                      <div className="grid grid-cols-3 gap-1">
                        {mediaFiles.images.slice(0, 6).map(img => (
                          <motion.div
                            key={img.id}
                            whileHover={{ scale: 1.04 }}
                            className="aspect-square rounded-lg overflow-hidden cursor-pointer bg-[#161f31]"
                            onClick={() => openPhoto(img.url)}
                          >
                            <img src={img.url} alt={t('friendInfoSidebar.mediaAlt')} className="w-full h-full object-cover" onError={e => { e.currentTarget.parentElement.style.display = 'none'; }} />
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-8 text-center">
                        <div className="w-10 h-10 rounded-2xl bg-[#161f31] flex items-center justify-center mb-2">
                          <ImageIcon className="w-5 h-5 text-[#1e2d47]" />
                        </div>
                        <p className="text-sm text-[#4d6a8a]">{t('friendInfoSidebar.noMediaYet')}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'files' && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest mb-2.5">{t('friendInfoSidebar.filesLabel')}</p>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-5 h-5 border-2 border-[#3d8ef0] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : mediaFiles.documents.length > 0 ? (
                      <div className="space-y-1.5">
                        {mediaFiles.documents.map(doc => {
                          const ext = doc.fileName.split('.').pop()?.toLowerCase();
                          const extColors = { pdf: 'text-red-400 bg-red-400/[0.08]', doc: 'text-blue-400 bg-blue-400/[0.08]', docx: 'text-blue-400 bg-blue-400/[0.08]', xls: 'text-green-400 bg-green-400/[0.08]', xlsx: 'text-green-400 bg-green-400/[0.08]', ppt: 'text-orange-400 bg-orange-400/[0.08]', pptx: 'text-orange-400 bg-orange-400/[0.08]' };
                          const colorClass = extColors[ext] || 'text-[#4d6a8a] bg-[#1e2d47]';
                          return (
                            <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-[#161f31] hover:bg-[#1e2d47] cursor-pointer transition-colors" onClick={() => window.open(doc.url, '_blank')}>
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                                <File className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-white truncate">{doc.fileName}</p>
                                <p className="text-[10px] text-[#4d6a8a] mt-0.5">{new Date(doc.createdAt).toLocaleDateString(dateLocaleTag)}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-8 text-center">
                        <div className="w-10 h-10 rounded-2xl bg-[#161f31] flex items-center justify-center mb-2">
                          <File className="w-5 h-5 text-[#1e2d47]" />
                        </div>
                        <p className="text-sm text-[#4d6a8a]">{t('friendInfoSidebar.noFilesYet')}</p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'notes' && (
                  <div>
                    <p className="text-[10px] font-semibold text-[#4d6a8a] uppercase tracking-widest mb-2.5">{t('friendInfoSidebar.sharedNotesLabel')}</p>
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="w-5 h-5 border-2 border-[#3d8ef0] border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : mediaFiles.notes.length > 0 ? (
                      <div className="space-y-1.5">
                        {mediaFiles.notes.map(note => (
                          <div key={note.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-[#161f31] hover:bg-[#1e2d47] transition-colors">
                            <div className="w-9 h-9 rounded-lg bg-[#3d8ef0]/[0.1] flex items-center justify-center flex-shrink-0">
                              <FileText className="w-4 h-4 text-[#3d8ef0]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-white truncate">{note.noteTitle}</p>
                              <p className="text-[10px] text-[#4d6a8a] mt-0.5">{new Date(note.createdAt).toLocaleDateString(dateLocaleTag)}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-col items-center py-8 text-center">
                        <div className="w-10 h-10 rounded-2xl bg-[#161f31] flex items-center justify-center mb-2">
                          <FileText className="w-5 h-5 text-[#1e2d47]" />
                        </div>
                        <p className="text-sm text-[#4d6a8a]">{t('friendInfoSidebar.noNotesYet')}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="mx-5 h-px bg-white/[0.05] mb-5" />

              {/* Bottom Actions */}
              <div className="px-5 pb-8 space-y-2.5">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#161f31] hover:bg-[#1a2640] transition-colors"
                >
                  <Trash2 className="w-4 h-4 text-[#4d6a8a] flex-shrink-0" />
                  <span className="text-sm text-[#8aaccc]">{t('friendInfoSidebar.clearChat')}</span>
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#161f31] hover:bg-red-500/[0.08] transition-colors"
                >
                  <LogOut className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-sm text-red-400">{t('friendInfoSidebar.deleteFriend')}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- All modals OUTSIDE motion.div to avoid transform stacking context ---- */}
      <ImageModal
        isOpen={showImageModal}
        onClose={() => { setShowImageModal(false); setSelectedImageUrl(null); }}
        imageUrl={selectedImageUrl}
      />

      <DeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteFriend}
        title={t('friendInfoSidebar.deleteFriend')}
        message={t('friendInfoSidebar.deleteFriendConfirmMessage', { email: friend.email })}
      />

      <DeleteModal
        isOpen={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        onConfirm={handleClearMessages}
        title={t('friendInfoSidebar.clearChat')}
        message={t('friendInfoSidebar.clearChatConfirmMessage')}
      />
    </>
  );

  if (typeof document !== 'undefined') {
    return createPortal(sidebarContent, document.body);
  }
  return sidebarContent;
};

export default FriendInfoSidebar;
